// Company admin panel — accessible to tenant admins (NOT Smeding,
// which uses the master-admin chain). Has 3 tabs: Team, Invitations,
// Billing.
import { useState, useEffect, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Users, Mail, CreditCard, Copy, Loader2, UserPlus, Crown, ChevronUp, ChevronDown, ArrowRightLeft, KeyRound, Trash2 } from 'lucide-react';
import { api } from '@/pages/planning/_helpers';

const P = '/company-admin';

export default function CompanyAdminPage() {
  const { user, isMasterAdmin } = useAuth();
  const [tab, setTab] = useState('team');
  const [info, setInfo] = useState(null);
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);

  const allowed = isMasterAdmin || user?.is_company_admin || user?.is_billing_owner;
  const load = useCallback(async () => {
    if (!allowed) return;
    setLoading(true);
    try {
      const [me, u, i, t] = await Promise.all([
        api(`${P}/me`),
        api(`${P}/users`),
        api(`${P}/invitations`),
        api(`${P}/billing-transfer`),
      ]);
      setInfo(me); setUsers(u || []); setInvites(i || []); setTransfers(t || []);
    } catch (e) { toast.error(e.message || 'Failed to load'); }
    finally { setLoading(false); }
  }, [allowed]);
  useEffect(() => { load(); }, [load]);

  // Billing + tenant admin surface: the platform owner (master admin),
  // the tenant's company admins, and billing owners can access it.
  if (!allowed) return <Navigate to="/dashboard" replace />;

  const promote = async (u) => { try { await api(`${P}/users/${u.user_id}/promote`, { method: 'POST' }); toast.success(`${u.name} promoted to company admin`); await load(); } catch (e) { toast.error(e.message); } };
  const demote  = async (u) => { try { await api(`${P}/users/${u.user_id}/demote`,  { method: 'POST' }); toast.success(`${u.name} demoted`); await load(); } catch (e) { toast.error(e.message); } };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="company-admin-page">
        <header>
          <h1 className="font-display text-2xl tracking-tight">Company admin</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage <strong>{user?.company}</strong> — your tenant. Subscription:{' '}
            <Badge variant="outline" data-testid="company-status-badge">{info?.subscription?.status || '—'}</Badge>
            {info?.subscription?.trial_ends_at && (
              <span className="ml-2 text-xs">Trial ends {info.subscription.trial_ends_at.slice(0, 10)}</span>
            )}
          </p>
        </header>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-4 w-full max-w-3xl">
            <TabsTrigger value="team" data-testid="ca-tab-team"><Users className="h-4 w-4 mr-2" />Team</TabsTrigger>
            <TabsTrigger value="invites" data-testid="ca-tab-invites"><Mail className="h-4 w-4 mr-2" />Invitations</TabsTrigger>
            <TabsTrigger value="billing" data-testid="ca-tab-billing"><CreditCard className="h-4 w-4 mr-2" />Billing</TabsTrigger>
            <TabsTrigger value="grants" data-testid="ca-tab-grants"><KeyRound className="h-4 w-4 mr-2" />Temp grants</TabsTrigger>
          </TabsList>

          {/* TEAM */}
          <TabsContent value="team" className="mt-6">
            <Card>
              <CardHeader><CardTitle className="text-base">Users in your tenant</CardTitle></CardHeader>
              <CardContent className="p-0">
                {loading ? <div className="p-6"><Loader2 className="h-4 w-4 animate-spin" /></div> : (
                  <table className="w-full text-sm">
                    <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
                      <tr><th className="px-4 py-3 text-left">Name</th><th className="px-4 py-3 text-left">Email</th><th className="px-4 py-3 text-left">Role</th><th className="px-4 py-3 text-right">Actions</th></tr>
                    </thead>
                    <tbody>
                      {users.map((u) => (
                        <tr key={u.user_id} className="border-t" data-testid={`ca-user-row-${u.user_id}`}>
                          <td className="px-4 py-3"><strong>{u.name}</strong>{u.is_billing_owner && <Badge variant="outline" className="ml-2 text-xs"><Crown className="h-3 w-3 mr-1" />Billing</Badge>}</td>
                          <td className="px-4 py-3 text-muted-foreground">{u.email}</td>
                          <td className="px-4 py-3">{u.is_company_admin ? <Badge>Company admin</Badge> : <Badge variant="secondary">User</Badge>}</td>
                          <td className="px-4 py-3 text-right">
                            {u.user_id !== user.user_id && !u.is_billing_owner && (
                              u.is_company_admin
                                ? <Button size="sm" variant="ghost" onClick={() => demote(u)} data-testid={`ca-demote-${u.user_id}`}><ChevronDown className="h-4 w-4 mr-1" />Demote</Button>
                                : <Button size="sm" variant="ghost" onClick={() => promote(u)} data-testid={`ca-promote-${u.user_id}`}><ChevronUp className="h-4 w-4 mr-1" />Promote</Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* INVITES */}
          <TabsContent value="invites" className="mt-6">
            <div className="flex justify-between items-center mb-3">
              <div className="text-sm text-muted-foreground">{invites.length} active invitation{invites.length === 1 ? '' : 's'}</div>
              <Button onClick={() => setInviteOpen(true)} data-testid="ca-invite-new"><UserPlus className="h-4 w-4 mr-2" />Invite user</Button>
            </div>
            <Card>
              <CardContent className="p-0">
                {invites.length === 0 ? (
                  <div className="py-8 text-center text-sm text-muted-foreground" data-testid="ca-invite-empty">No pending invitations.</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
                      <tr><th className="px-4 py-3 text-left">Email</th><th className="px-4 py-3 text-left">Role</th><th className="px-4 py-3 text-left">Code</th><th className="px-4 py-3 text-left">Expires</th><th className="px-4 py-3 text-right">Actions</th></tr>
                    </thead>
                    <tbody>
                      {invites.map((i) => (
                        <tr key={i.invitation_id} className="border-t" data-testid={`ca-invite-row-${i.invitation_id}`}>
                          <td className="px-4 py-3">{i.email}</td>
                          <td className="px-4 py-3"><Badge variant={i.grants_company_admin ? 'default' : 'secondary'}>{i.grants_company_admin ? 'Admin' : 'User'}</Badge></td>
                          <td className="px-4 py-3 font-mono text-xs">{i.code}</td>
                          <td className="px-4 py-3 text-xs text-muted-foreground">{i.expires_at?.slice(0, 10)}</td>
                          <td className="px-4 py-3 text-right">
                            <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(i.code); toast.success('Code copied'); }} data-testid={`ca-invite-copy-${i.invitation_id}`}><Copy className="h-4 w-4" /></Button>
                            <Button size="sm" variant="ghost" onClick={async () => { try { await api(`${P}/invitations/${i.invitation_id}`, { method: 'DELETE' }); toast.success('Revoked'); await load(); } catch (e) { toast.error(e.message); } }} data-testid={`ca-invite-revoke-${i.invitation_id}`}>Revoke</Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* BILLING */}
          <TabsContent value="billing" className="mt-6 space-y-4">
            <Card>
              <CardHeader><CardTitle className="text-base">Subscription</CardTitle></CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Status</span><Badge variant="outline">{info?.subscription?.status || '—'}</Badge></div>
                {info?.subscription?.trial_ends_at && (
                  <div className="flex justify-between"><span className="text-muted-foreground">Trial ends</span><span>{info.subscription.trial_ends_at.slice(0, 10)}</span></div>
                )}
                <div className="flex justify-between"><span className="text-muted-foreground">Billing owner</span><span>{users.find((u) => u.is_billing_owner)?.email || '—'}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Plan</span><span>{info?.subscription?.plan_name || 'Trial'}</span></div>
                {info?.company?.subscription_amount_cents && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Subscription price</span>
                      <span className="font-semibold">
                        {(info.company.subscription_amount_cents / 100).toFixed(2)} {(info.company.subscription_currency || 'eur').toUpperCase()}
                        <span className="text-muted-foreground font-normal">/{info.company.subscription_interval === 'year' ? 'yr' : 'mo'}</span>
                      </span>
                    </div>
                    {info.company.discount_percent && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Intro discount</span>
                        <span className="text-amber-600 font-medium">−{info.company.discount_percent}% for {info.company.discount_cycles} {info.company.subscription_interval === 'year' ? 'year(s)' : 'month(s)'}</span>
                      </div>
                    )}
                  </>
                )}
                {info?.subscription?.status !== 'active' && (
                  <div className="pt-3">
                    {info?.company?.subscription_amount_cents ? (
                      <Button
                        className="w-full"
                        onClick={async () => {
                          try {
                            const r = await api('/payments/checkout/session', {
                              method: 'POST',
                              body: JSON.stringify({ origin_url: window.location.origin }),
                            });
                            window.location.href = r.url;
                          } catch (e) { toast.error(e.message); }
                        }}
                        data-testid="ca-pay-activate"
                      >
                        Subscribe — auto-billed {info.company.subscription_interval === 'year' ? 'yearly' : 'monthly'}
                      </Button>
                    ) : (
                      <p className="text-xs text-muted-foreground">
                        No price configured yet. Email{' '}
                        <a className="underline" href="mailto:info@kaizenkanri.nl">info@kaizenkanri.nl</a>{' '}
                        for your tailored quote — once we set it, this card will switch to a Stripe checkout button.
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row justify-between items-center">
                <CardTitle className="text-base">Billing transfer requests</CardTitle>
                <Button onClick={() => setTransferOpen(true)} data-testid="ca-transfer-new"><ArrowRightLeft className="h-4 w-4 mr-2" />Request transfer</Button>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-muted-foreground mb-3">
                  Job changes? Transferring billing requires approval from <a href="mailto:info@kaizenkanri.nl" className="underline">info@kaizenkanri.nl</a>. Submit the new payer's details below — once approved, the change applies automatically.
                </p>
                {transfers.length === 0 ? (
                  <div className="text-sm text-muted-foreground" data-testid="ca-transfer-empty">No transfer requests yet.</div>
                ) : (
                  <ul className="space-y-2 text-sm">
                    {transfers.map((t) => (
                      <li key={t.id} className="border rounded p-3 flex flex-wrap justify-between gap-2" data-testid={`ca-transfer-row-${t.id}`}>
                        <div>
                          <strong>{t.new_payer_name}</strong> &lt;{t.new_payer_email}&gt;
                          <div className="text-xs text-muted-foreground">Submitted {t.created_at?.slice(0, 10)}</div>
                        </div>
                        <Badge variant={t.status === 'approved' ? 'default' : t.status === 'rejected' ? 'destructive' : 'secondary'}>{t.status}</Badge>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* TEMP GRANTS — gives a selected admin time-bounded write access to Calendar + Shifts */}
          <TabsContent value="grants" className="mt-6">
            <TempGrantsPanel users={users} />
          </TabsContent>
        </Tabs>

        <InviteDialog open={inviteOpen} onOpenChange={setInviteOpen} onSaved={load} />
        <TransferDialog open={transferOpen} onOpenChange={setTransferOpen} users={users} onSaved={load} />
      </div>
    </DashboardLayout>
  );
}


function InviteDialog({ open, onOpenChange, onSaved }) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('user');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const r = await api(`${P}/invitations`, { method: 'POST', body: JSON.stringify({ email, role }) });
      toast.success(`Invitation ${r.code} created`);
      setEmail(''); setRole('user');
      onOpenChange(false);
      await onSaved();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="ca-invite-dialog">
        <DialogHeader>
          <DialogTitle>Invite a teammate</DialogTitle>
          <DialogDescription>They'll receive a one-time code to register into your tenant.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Input type="email" required placeholder="teammate@yourco.com" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="ca-invite-email" />
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger data-testid="ca-invite-role"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="user">User</SelectItem>
              <SelectItem value="admin">Company admin (can invite + manage)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={!email || busy} data-testid="ca-invite-submit">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Send invitation</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function TransferDialog({ open, onOpenChange, users, onSaved }) {
  const [form, setForm] = useState({ new_payer_name: '', new_payer_email: '', new_payer_user_id: '__none__', company_legal_name: '', iban: '', vat_number: '', billing_address: '', reason: '' });
  const [busy, setBusy] = useState(false);
  useEffect(() => { setForm({ new_payer_name: '', new_payer_email: '', new_payer_user_id: '__none__', company_legal_name: '', iban: '', vat_number: '', billing_address: '', reason: '' }); }, [open]);
  const submit = async () => {
    setBusy(true);
    try {
      const body = { ...form, new_payer_user_id: form.new_payer_user_id === '__none__' ? null : form.new_payer_user_id };
      await api(`${P}/billing-transfer`, { method: 'POST', body: JSON.stringify(body) });
      toast.success('Transfer request sent to info@kaizenkanri.nl');
      onOpenChange(false);
      await onSaved();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="ca-transfer-dialog">
        <DialogHeader>
          <DialogTitle>Request billing transfer</DialogTitle>
          <DialogDescription>Fill in the new payer's details. info@kaizenkanri.nl reviews every request before it applies.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="text-xs text-muted-foreground">Existing user in your tenant (optional)</label>
            <Select value={form.new_payer_user_id} onValueChange={(v) => setForm({ ...form, new_payer_user_id: v })}>
              <SelectTrigger data-testid="ca-transfer-user"><SelectValue placeholder="External payer" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— External payer —</SelectItem>
                {users.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name} · {u.email}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><label className="text-xs text-muted-foreground">New payer name</label><Input value={form.new_payer_name} onChange={(e) => setForm({ ...form, new_payer_name: e.target.value })} data-testid="ca-transfer-name" /></div>
          <div><label className="text-xs text-muted-foreground">New payer email</label><Input type="email" value={form.new_payer_email} onChange={(e) => setForm({ ...form, new_payer_email: e.target.value })} data-testid="ca-transfer-email" /></div>
          <div className="col-span-2"><label className="text-xs text-muted-foreground">Company legal name (optional)</label><Input value={form.company_legal_name} onChange={(e) => setForm({ ...form, company_legal_name: e.target.value })} /></div>
          <div><label className="text-xs text-muted-foreground">IBAN (optional)</label><Input value={form.iban} onChange={(e) => setForm({ ...form, iban: e.target.value })} /></div>
          <div><label className="text-xs text-muted-foreground">VAT (optional)</label><Input value={form.vat_number} onChange={(e) => setForm({ ...form, vat_number: e.target.value })} /></div>
          <div className="col-span-2"><label className="text-xs text-muted-foreground">Billing address (optional)</label><Input value={form.billing_address} onChange={(e) => setForm({ ...form, billing_address: e.target.value })} /></div>
          <div className="col-span-2"><label className="text-xs text-muted-foreground">Reason (optional)</label><Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="e.g. previous admin left the company" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={!form.new_payer_name || !form.new_payer_email || busy} data-testid="ca-transfer-submit">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Submit request</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ── Temporary Planning Grants — Master-only mini-CRUD that hands a
// selected admin time-bounded write access to Calendar + Shifts.
function TempGrantsPanel({ users }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const inDays = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };
  const [form, setForm] = useState({ user_id: '', scopes: { shifts: true, calendar: true }, start_date: today, end_date: inDays(7) });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setList(await api('/planning-v2/temp-grants') || []); }
    catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    setBusy(true);
    try {
      const u = users.find((x) => x.user_id === form.user_id);
      const scopes = Object.entries(form.scopes).filter(([, v]) => v).map(([k]) => k);
      if (!scopes.length) throw new Error('Pick at least one scope');
      await api('/planning-v2/temp-grants', { method: 'POST', body: JSON.stringify({
        user_id: form.user_id, user_email: u?.email,
        scopes, start_date: form.start_date, end_date: form.end_date,
      }) });
      toast.success(`Granted ${scopes.join(' + ')} to ${u?.name || form.user_id}`);
      setOpen(false);
      setForm({ user_id: '', scopes: { shifts: true, calendar: true }, start_date: today, end_date: inDays(7) });
      load();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };
  const revoke = async (g) => {
    try { await api(`/planning-v2/temp-grants/${g.id}`, { method: 'DELETE' }); toast.success('Grant revoked'); load(); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <Card data-testid="ca-temp-grants">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base">Temporary planning grants</CardTitle>
        <Button size="sm" onClick={() => setOpen(true)} data-testid="ca-grant-new"><UserPlus className="h-4 w-4 mr-2" />Grant access</Button>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-muted-foreground">Hand a selected admin time-bounded write access to <strong>Calendar</strong> and/or <strong>Shifts</strong> (add/edit/cancel shifts, assign workers). Other Planning v2 modules stay master-only.</p>
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : list.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="ca-grants-empty">No active grants.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
              <tr><th className="px-3 py-2 text-left">User</th><th className="px-3 py-2 text-left">Scopes</th><th className="px-3 py-2 text-left">From</th><th className="px-3 py-2 text-left">To</th><th className="px-3 py-2 text-right">Actions</th></tr>
            </thead>
            <tbody>
              {list.map((g) => (
                <tr key={g.id} className="border-t" data-testid={`ca-grant-row-${g.id}`}>
                  <td className="px-3 py-2">{g.user_email || g.user_id}</td>
                  <td className="px-3 py-2 flex gap-1">{g.scopes.map((s) => <Badge key={s} variant="outline">{s}</Badge>)}</td>
                  <td className="px-3 py-2 tabular-nums">{g.start_date}</td>
                  <td className="px-3 py-2 tabular-nums">{g.end_date}</td>
                  <td className="px-3 py-2 text-right"><Button variant="ghost" size="sm" onClick={() => revoke(g)} className="text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/40" data-testid={`ca-grant-revoke-${g.id}`}><Trash2 className="h-4 w-4" /></Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="ca-grant-dialog">
          <DialogHeader>
            <DialogTitle>Grant temporary planning access</DialogTitle>
            <DialogDescription>Pick a user, the scopes, and the active window. They&apos;ll regain write access automatically when the window ends.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="text-xs text-muted-foreground">User</label>
              <Select value={form.user_id} onValueChange={(v) => setForm({ ...form, user_id: v })}>
                <SelectTrigger data-testid="ca-grant-user"><SelectValue placeholder="Select a user…" /></SelectTrigger>
                <SelectContent>
                  {users.filter((u) => !u.is_master_admin).map((u) => (
                    <SelectItem key={u.user_id} value={u.user_id}>{u.name} · {u.email}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-4">
              <label className="inline-flex items-center gap-1 text-sm cursor-pointer">
                <input type="checkbox" checked={form.scopes.calendar} onChange={(e) => setForm({ ...form, scopes: { ...form.scopes, calendar: e.target.checked } })} data-testid="ca-grant-scope-calendar" />
                Calendar
              </label>
              <label className="inline-flex items-center gap-1 text-sm cursor-pointer">
                <input type="checkbox" checked={form.scopes.shifts} onChange={(e) => setForm({ ...form, scopes: { ...form.scopes, shifts: e.target.checked } })} data-testid="ca-grant-scope-shifts" />
                Shifts (assign/unassign workers, edit/cancel)
              </label>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground">Start date</label>
                <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="ca-grant-from" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">End date</label>
                <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="ca-grant-to" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={!form.user_id || busy} data-testid="ca-grant-submit">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Grant</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}